import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/shared/auth/AuthContext";
import { hasAnyPermission, hasPermission } from "@/shared/auth/permissions";
import { LoadingState, NoPermissionState } from "@/shared/ui";
import { landingPathForUser } from "@/modules/auth/domain/landing";
import {
  canAccessPath,
  canAccessShell,
  shellForPath,
  type AccessShell,
} from "@/modules/auth/domain/shellAccess";
import { readReturnTo } from "@/shared/shell/returnTo";
import { authRoleCodes } from "@/shared/auth/types";

export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "bootstrapping") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label="Đang khởi tạo phiên…" />
      </div>
    );
  }

  if (status !== "authenticated") {
    return <Navigate to="/auth/login" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}

export function GuestOnly() {
  const { status, user } = useAuth();

  if (status === "bootstrapping") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  if (status === "authenticated" && user) {
    return <Navigate to={landingPathForUser(user.roles)} replace />;
  }

  return <Outlet />;
}

/**
 * Universal shell isolation for **all** authenticated shells (Ops · Field · EXE).
 * Hard URL / deep-link to a shell outside `ROLE_SHELLS` → falls back to safe returnTo or persona landing.
 * Print (`/print/*`) is not a channel shell — not gated here.
 */
export function RequireShellAccess({ shell }: { shell?: AccessShell } = {}) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/auth/login" replace />;

  const targetShell = shell ?? shellForPath(location.pathname);
  const allowed = targetShell
    ? canAccessShell(user.roles, targetShell)
    : canAccessPath(user.roles, location.pathname);

  if (!allowed) {
    const returnTo = readReturnTo(location.state);
    const fallback =
      returnTo && canAccessPath(user.roles, returnTo)
        ? returnTo
        : landingPathForUser(user.roles);
    return <Navigate to={fallback} replace />;
  }

  return <Outlet />;
}

/** `/` → landing when signed in; else login. */
export function RootRedirect() {
  const { status, user } = useAuth();
  if (status === "bootstrapping") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState />
      </div>
    );
  }
  if (status === "authenticated" && user) {
    return <Navigate to={landingPathForUser(user.roles)} replace />;
  }
  return <Navigate to="/auth/login" replace />;
}

export function RequirePermissions({
  codes,
  roles,
  match = "all",
}: {
  codes: readonly string[];
  roles?: readonly string[];
  /** `all` = every code required; `any` = at least one (BE ENG read = manage∨approve). */
  match?: "all" | "any";
}) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth/login" replace />;
  if (codes.length === 0 && (!roles || roles.length === 0)) return <Outlet />;
  const userRoles = authRoleCodes(user.roles ?? []);
  const roleOk = roles ? roles.some((r) => userRoles.includes(r.toUpperCase())) : false;
  if (roleOk) return <Outlet />;
  const ok =
    match === "any"
      ? hasAnyPermission(user.permissions, codes)
      : codes.every((c) => hasPermission(user.permissions, c));
  if (!ok) return <NoPermissionState />;
  return <Outlet />;
}
